import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import Dropzone, {
  IInputProps,
  ILayoutProps,
  IPreviewProps,
} from "react-dropzone-uploader";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { downloadS3File } from "../../utils/DownloadFile";
import ConversionTableGrid, { ConversionDto } from "./ConversionAgTable";
import { AgGridTransactionsCaptable } from "../../types/AllTransactionModel";
import AGGridRoundsTable from "../rounds/RoundsAGTable";
import { useAuthStore } from "../../store";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import { useInvGetInstrumentAndShareHolder } from "../../queries/investorPortal";
import {
  useGetInstrumentAndShareHolder,
  useGetQuickRoundPreview,
} from "../../queries/quickRound";
import { useShareholderInstrumentStore } from "../../store/shareholderInstrumentStore";
import { useApproveTransactionPreview } from "../../queries/transactionRound";
import { queryClient } from "../../queries/client";
import AGGridShareholderQuickRoundTable from "./ShareholderAgGridCaptableQuickRound";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { HStack } from "../../components/utils";

export type ConversionTableInput = {
  data: string[][];
  file: File;
  id: number;
  indexData: any;
  primaryColumns?: number[];
  secondaryColumns?: number[];
};

export type ConversionTableSaved = {
  id?: number;
  name: string;
  description: string;
  tableData: any;
  fileUrl?: string;
  fileName?: string;
  isPublished?: boolean;
};

type QuickAddConversionProps = {
  onSave?: (data: ConversionTableSaved) => void;
  onCancel?: () => void;
  isSuperAdmin?: boolean;
  filterData?: any;
  saveFilter?: (v: any) => void;
  removeFilter?: (v: any) => void;
  publishTable?: (v: any) => void;
  loaders?: any;
};

const QuickAddConversion: React.FC<QuickAddConversionProps> = (props) => {
  const { id, cId, token, id: paramId, investorId } = useParams();
  const [currentEventId, setCurrentEventId] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    if (paramId && !currentEventId) {
      setCurrentEventId(paramId);
      setShowPreview(true);
    }
  }, [paramId, currentEventId]);
  const companyId = useAuthStore.getState().companyId || "";
  const authToken = useAuthStore.getState().accessToken || "";
  const [file, setFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<string[][]>([]);
  const [conversionData, setConversionData] = useState<ConversionDto[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const navigate = useNavigate();

  const { mutate: approveTransaction } = useApproveTransactionPreview();
  const {
    refetch: fetchPreview,
    data: previewData,
    isLoading: previewLoading,
    isError: previewError,
  } = useGetQuickRoundPreview(currentEventId!);

  const {
    refetch: getInstrumentAndShareHolder,
    data: instrumentShareholderInfoData,
  } = cId
    ? useInvGetInstrumentAndShareHolder(cId, token || "")
    : useGetInstrumentAndShareHolder(companyId, cId);

  const shareholderAndInstrumentstore = useShareholderInstrumentStore();

  useEffect(() => {
    if (
      cId ||
      (shareholderAndInstrumentstore.shareholders.length === 0 &&
        shareholderAndInstrumentstore.instrumentClasses.length === 0)
    )
      getInstrumentAndShareHolder().then((data) => {
        shareholderAndInstrumentstore.setInstrumentClass(
          data?.data?.instrumentClasses || []
        );
        shareholderAndInstrumentstore.setShareholders(
          data?.data?.shareholders || []
        );
        shareholderAndInstrumentstore.setInstrumentClassSubTypes(
          data?.data?.instrumentClassSubTypes || {}
        );
      });
  }, []);

  useEffect(() => {
    if (currentEventId && showPreview) {
      fetchPreview();
    }
  }, [currentEventId, showPreview, fetchPreview]);

  const parseExcel = async (file: File) => {
    try {
      setLoading(true);

      const buffer = await file.arrayBuffer();

      const workbook = XLSX.read(buffer, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        toast.error("No sheets found in the Excel file.");
        setLoading(false);
        return;
      }

      const sheet = workbook.Sheets[sheetName];

      const rawData: any[][] = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        blankrows: false,
      });

      const data: string[][] = rawData.map((row) =>
        row.map((cell) => {
          if (cell === null || cell === undefined) return "";
          return cell.toString().trim();
        })
      );

      const hasContent =
        data.length > 0 && data.some((row) => row.some((cell) => cell !== ""));

      if (!hasContent) {
        toast.error("Excel file is empty or contains no data.");
        setLoading(false);
        return;
      }

      setExcelData(data);
      setFile(file);

      const eventId = await uploadConversionFile(file);

      if (!eventId) {
        setLoading(false);
        return;
      }

      setCurrentEventId(eventId);
      setShowPreview(true);
    } catch (err: any) {
      console.error("Parse error:", err);
      toast.error(
        err?.message ||
          "Failed to read Excel file. Please ensure it's a valid .xlsx or .xls file."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = ({ meta, file }: any, status: string) => {
    if (status === "done") {
      parseExcel(file);
    } else if (status === "removed") {
      handleDelete();
    }
  };

  const handleDelete = () => {
    setFile(null);
    setExcelData([]);
    setConversionData([]);
    setShowPreview(false);
    setCurrentEventId(undefined);
  };

  const handleClear = () => {
    handleDelete();
    setDropzoneKey((prev) => prev + 1);
  };

  const handleDataChange = (data: ConversionDto[]) => {
    setConversionData(data);
  };

  const handleSave = () => {
    props.onSave?.({
      name: "Conversion Table",
      description: "",
      tableData: {
        data: excelData,
        indexData: excelData,
        conversionData,
      },
      fileName: file?.name,
    });
    handleDelete();
    setDropzoneKey((prev) => prev + 1);
  };

  const handleBack = () => {
    handleDelete();
    props.onCancel?.();
  };

  const uploadConversionFile = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const baseUrl =
        process.env.REACT_APP_NEW_CAPTABLE_API || "http://localhost:4000";

      const response = await axios.post(
        `${baseUrl}v1/company/transaction/bulkConversionsExcelUpload`,
        formData,
        {
          headers: {
            Authorization: authToken || "",
            companyId: companyId || "",
          },
        }
      );

      const newEventId = response.data?.data?.id;
      if (!newEventId) {
        toast.error("Upload succeeded but no event ID returned.");
        return null;
      }
      toast.success("File uploaded & processed successfully!");
      return newEventId;
    } catch (error: any) {
      console.error("Upload error:", error);
      const msg =
        error.response?.data?.errorMessage ||
        error.response?.data?.message ||
        error.response?.data?.reason ||
        "Failed to upload and process file.";
      toast.error(msg);
      return null;
    }
  };
  const handleApprove = () => {
    if (!previewData) {
      toast.error("No valid conversion data to approve.");
      return;
    }

    if (!currentEventId) {
      toast.error("Event ID is missing. Cannot approve conversion.");
      return;
    }

    setApproveLoading(true);

    approveTransaction(
      {
        companyId,
        eventId: currentEventId,
        pricePerShare: previewData?.eventInfo?.pps || 0,
        valuation: previewData?.eventInfo?.valuation || 0,
      },
      {
        onSuccess: () => {
          toast.success("Conversion approved successfully!", {
            autoClose: 3000,
          });

          queryClient.invalidateQueries({ queryKey: ["getAllEvents"] });
          queryClient.invalidateQueries({ queryKey: ["getAllTransactions"] });
          queryClient.invalidateQueries({
            queryKey: ["getInstrumentAndShareHolder"],
          });
          queryClient.invalidateQueries({
            queryKey: ["getSecurityOverviewDetails"],
          });
          queryClient.invalidateQueries({
            queryKey: ["getShareholderOverview"],
          });
          queryClient.invalidateQueries({ queryKey: ["proformaEventSummary"] });

          handleDelete();
          setDropzoneKey((prev) => prev + 1);
          navigate("/landing/dashboard");
        },
        onError: (err: any) => {
          setApproveLoading(false);
          const msg =
            err.response?.data?.reason || "Failed to approve conversion";
          toast.error(msg, { autoClose: 5000 });
        },
        onSettled: () => {
          setApproveLoading(false);
        },
      }
    );
  };

  const Preview = ({ meta }: IPreviewProps) => (
    <div className="preview-box w-full px-4 py-3">
      <div className="flex justify-between items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="truncate">
            <span className="name font-medium">{meta.name}</span> -{" "}
            <span className="status text-sm">{meta.status}</span>
            {meta.status !== "done" && (
              <span className="percent text-sm">
                ({Math.round(meta.percent)}%)
              </span>
            )}
          </div>
        </div>
        {meta.status === "done" && (
          <button
            onClick={handleClear}
            className="flex-shrink-0 p-1 hover:bg-red-100 rounded-full transition-colors"
            type="button"
            title="Clear file"
          >
            <Icon icon="mdi:close" width={20} className="text-red-600" />
          </button>
        )}
      </div>
      <div className="h-1 w-full bg-gray-300 mt-2 rounded">
        <div
          style={{ width: `${meta.percent}%` }}
          className={`h-full rounded transition-all ${
            meta.percent < 70 ||
            meta.status === "error_upload" ||
            meta.status === "exception_upload"
              ? "bg-red-600"
              : "bg-green-600"
          }`}
        ></div>
      </div>
      {loading && (
        <div className="flex text-orange-600 text-center w-full justify-center mt-2 gap-2">
          <p
            className="indicator-progress text-sm"
            style={{ display: "block" }}
          >
            Please wait...
          </p>
          <Icon className="animate-spin" icon="lucide:loader-2" width={18} />
        </div>
      )}
    </div>
  );
  const Input = ({
    accept,
    onFiles,
    files,
    getFilesFromEvent,
  }: IInputProps) => {
    const isFileSelected = file !== null || files.length > 0;
    return (
      <div>
        <div className="text-center p-4">
          <h6 className="text-gray-600 text-lg font-semibold">
            Upload Excel for Conversion Table
          </h6>
        </div>
        <div
          className={`flex flex-row mx-auto
   w-72 border-dashed border-2 text-center items-center ${
     isFileSelected
       ? "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60"
       : "bg-pink-100 hover:border-orange-600"
   }`}
        >
          <label
            className={isFileSelected ? "cursor-not-allowed" : "cursor-pointer"}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              disabled={isFileSelected}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onFiles(files);
              }}
            />
            <div className="py-4 px-20 flex">
              <div>
                <Icon icon="material-symbols:cloud-upload" height="30" />
              </div>
              <h3 className="py-1 px-2">Select File</h3>
            </div>
          </label>
        </div>
        <p className="text-xs pt-1 text-center">
          download the excel template{" "}
          <span
            onClick={() =>
              downloadS3File(
                "https://equion-dev.s3.us-west-2.amazonaws.com/captable-templates/Conversion-template.xlsx"
              )
            }
            className="underline text-blue-600 font-medium cursor-pointer"
          >
            here
          </span>
        </p>
      </div>
    );
  };

  const Layout = ({ input, previews, dropzoneProps }: ILayoutProps) => (
    <div className="my-2">
      <div {...dropzoneProps}>
        {input}
        {previews}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
        <Dropzone
          key={dropzoneKey}
          onChangeStatus={handleChangeStatus}
          multiple={false}
          maxFiles={1}
          PreviewComponent={Preview}
          maxSizeBytes={100000000}
          InputComponent={Input}
          autoUpload={true}
          LayoutComponent={Layout}
          accept=".xlsx,.xls"
        />
      </div>

      {previewData?.transactions && (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <ConversionTableGrid
              previewData={previewData?.transactions as unknown as any[]}
              onDataChange={handleDataChange}
              isEditable={true}
              isShareholder ={!!cId}
            />
          </div>

          {currentEventId && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {previewLoading && (
                <div className="flex items-center justify-center py-8 text-gray-600">
                  <Icon
                    className="animate-spin mr-2"
                    icon="lucide:loader-2"
                    width={24}
                  />
                  <span>Loading preview...</span>
                </div>
              )}

              {previewError && (
                <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-lg text-sm">
                  Error loading preview data. Please try again.
                </div>
              )}

              {previewData?.holdingDifference && (
                <AGGridShareholderQuickRoundTable
                  data={previewData?.holdingDifference || []}
                />
              )}

              {!previewLoading && !previewError && !previewData && (
                <div className="text-center py-8 text-gray-500">
                  No preview data available
                </div>
              )}
            </div>
          )}
          {/* <HStack className="justify-end gap-4 mt-4">
          <div className="flex gap-4 py-6 bg-gray-50 -mx-4 px-8 -mb-8 rounded-b-lg">
            <SecondaryCTAButton
              event-name="Exisiting Investor Discard"
              buttonSize={ButtonSize.SMALL}
              onClick={() => {
                handleDelete();
                setCurrentEventId(undefined);
                setDropzoneKey((prev) => prev + 1);
              }}
            >
              Discard
            </SecondaryCTAButton>
          </div>
          <div className="flex justify-end gap-4 py-6 bg-gray-50 -mx-4 px-8 -mb-8 rounded-b-lg">
            <PrimaryCTAButton
              event-name="Approve Conversion Details"
              buttonSize={ButtonSize.SMALL}
              onClick={handleApprove}
            >
              Approve
            </PrimaryCTAButton>
          </div>
          </HStack> */}
          <HStack className="justify-between mt-2 w-full">
            {/* LEFT BUTTON */}
            <div className="py-6 bg-gray-50 -mx-4 px-8 -mb-8 rounded-b-lg">
              <SecondaryCTAButton
                event-name="Existing Investor Discard"
                buttonSize={ButtonSize.SMALL}
                onClick={() => {
                  handleDelete();
                  setCurrentEventId(undefined);
                  setDropzoneKey((prev) => prev + 1);
                }}
              >
                Discard
              </SecondaryCTAButton>
            </div>

            {/* RIGHT BUTTON */}
            <div className="py-6 bg-gray-50 -mx-4 px-8 -mb-8 rounded-b-lg">
              <PrimaryCTAButton
                event-name="Approve Conversion Details"
                buttonSize={ButtonSize.SMALL}
                onClick={handleApprove}
              >
                Approve
              </PrimaryCTAButton>
            </div>
          </HStack>
        </div>
      )}
    </div>
  );
};

export default QuickAddConversion;
